import { Constants } from "@babylonjs/core/Engines/constants";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Scene } from "@babylonjs/core/scene";
import { MmdStandardMaterial } from "./mmdStandardMaterial";
import { PmxObject } from "./Parser/pmxObject";
import { StandardMaterialBuilderBase } from "./standardMaterialBuilder";
/**
 * MMD standard material builder
 *
 * Use `MmdStandardMaterial` to create a mmdmesh material
 */
export class MmdStandardMaterialBuilder extends StandardMaterialBuilderBase {
    constructor() {
        super(MmdStandardMaterial);
    }
    _buildTextureNameMap(materialsInfo, materials, imagePathTable, texturesInfo, textureNameMap) {
        for (let i = 0; i < materialsInfo.length; ++i) {
            const materialInfo = materialsInfo[i];
            const material = materials[i];
            const diffuseTexturePath = imagePathTable[texturesInfo[materialInfo.textureIndex]?.imagePathIndex];
            if (diffuseTexturePath !== undefined) {
                const diffuseTexture = material.diffuseTexture;
                if (diffuseTexture !== null) {
                    textureNameMap.set(diffuseTexture, diffuseTexturePath);
                }
            }
            const sphereTexturePath = imagePathTable[texturesInfo[materialInfo.sphereTextureIndex]?.imagePathIndex];
            if (sphereTexturePath !== undefined) {
                const sphereTexture = material.sphereTexture;
                if (sphereTexture !== null) {
                    textureNameMap.set(sphereTexture, sphereTexturePath);
                }
            }
            const toonTexturePath = imagePathTable[texturesInfo[materialInfo.toonTextureIndex]?.imagePathIndex];
            if (toonTexturePath !== undefined) {
                const toonTexture = material.toonTexture;
                if (toonTexture !== null) {
                    textureNameMap.set(toonTexture, toonTexturePath);
                }
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    async loadSphereTexture(uniqueId, material, materialInfo, imagePathTable, textureInfo, scene, assetContainer, rootUrl, referenceFileResolver, logger, onTextureLoadComplete) {
        if (materialInfo.sphereTextureMode !== PmxObject.Material.SphereTextureMode.Off) {
            const sphereTexturePath = imagePathTable[textureInfo?.imagePathIndex ?? -1];
            if (sphereTexturePath !== undefined) {
                const format = scene.getEngine().isWebGPU || materialInfo.sphereTextureMode === PmxObject.Material.SphereTextureMode.Multiply
                    ? Constants.TEXTUREFORMAT_RGBA
                    : Constants.TEXTUREFORMAT_RGB; // Maybe we should not use RGB format for performance reasons
                const sphereTextureFileFullPath = referenceFileResolver.createFullPath(sphereTexturePath);
                let sphereTexture;
                const file = referenceFileResolver.resolve(sphereTextureFileFullPath);
                if (file !== undefined) {
                    sphereTexture = (await this._textureLoader.loadTextureFromBufferAsync(uniqueId, sphereTextureFileFullPath, file instanceof File ? file : file.data, scene, assetContainer, {
                        ...textureInfo,
                        deleteBuffer: this.deleteTextureBufferAfterLoad,
                        format: format,
                        mimeType: file instanceof File ? file.type : file.mimeType,
                        forcedExtension: this._getForcedExtension(sphereTexturePath)
                    }));
                }
                else {
                    sphereTexture = (await this._textureLoader.loadTextureAsync(uniqueId, rootUrl, sphereTexturePath, scene, assetContainer, {
                        ...textureInfo,
                        deleteBuffer: this.deleteTextureBufferAfterLoad,
                        format: format,
                        forcedExtension: this._getForcedExtension(sphereTexturePath)
                    }));
                }
                if (sphereTexture !== null) {
                    material.sphereTexture = sphereTexture;
                    material.sphereTextureBlendMode = materialInfo.sphereTextureMode;
                }
                else {
                    logger.error(`Failed to load sphere texture: ${sphereTextureFileFullPath}`);
                }
                onTextureLoadComplete?.();
            }
            else {
                onTextureLoadComplete?.();
            }
        }
        else {
            onTextureLoadComplete?.();
        }
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    async loadToonTexture(uniqueId, material, materialInfo, imagePathTable, textureInfo, scene, assetContainer, rootUrl, referenceFileResolver, logger, onTextureLoadComplete) {
        let toonTexturePath;
        if (materialInfo.isSharedToonTexture) {
            toonTexturePath = materialInfo.toonTextureIndex;
        }
        else {
            toonTexturePath = imagePathTable[textureInfo?.imagePathIndex ?? -1];
        }
        if (toonTexturePath !== undefined) {
            const toonTextureFileFullPath = referenceFileResolver.createFullPath(toonTexturePath.toString());
            let toonTexture;
            const file = typeof toonTexturePath === "string" ? referenceFileResolver.resolve(toonTextureFileFullPath) : undefined;
            if (file !== undefined) {
                toonTexture = (await this._textureLoader.loadTextureFromBufferAsync(uniqueId, toonTextureFileFullPath, file instanceof File ? file : file.data, scene, assetContainer, {
                    ...textureInfo,
                    deleteBuffer: this.deleteTextureBufferAfterLoad,
                    format: scene.getEngine().isWebGPU ? Constants.TEXTUREFORMAT_RGBA : Constants.TEXTUREFORMAT_RGB,
                    mimeType: file instanceof File ? file.type : file.mimeType
                }));
            }
            else {
                toonTexture = (await this._textureLoader.loadTextureAsync(uniqueId, rootUrl, toonTexturePath, scene, assetContainer, {
                    ...textureInfo,
                    deleteBuffer: this.deleteTextureBufferAfterLoad,
                    format: scene.getEngine().isWebGPU ? Constants.TEXTUREFORMAT_RGBA : Constants.TEXTUREFORMAT_RGB
                }));
            }
            if (toonTexture !== null) {
                material.toonTexture = toonTexture;
            }
            else {
                logger.error(`Failed to load toon texture: ${toonTextureFileFullPath}`);
            }
            onTextureLoadComplete?.();
        }
        else {
            onTextureLoadComplete?.();
        }
    }
    /**
     * Load outline rendering properties
     *
     * This method can be overridden for customizing the material loading process
     * @param material Material
     * @param materialInfo Material information
     * @param logger Logger
     */
    loadOutlineRenderingProperties(material, materialInfo, logger) {
        if (materialInfo.flag & PmxObject.Material.Flag.EnabledToonEdge) {
            if (Scene.prototype.getMmdOutlineRenderer === undefined) {
                logger.warn("MMD Outline Renderer is not available. Please import \"babylon-mmd/esm/Loader/mmdOutlineRenderer\".");
            }
            material.renderOutline = true;
            material.outlineWidth = materialInfo.edgeSize;
            const edgeColor = materialInfo.edgeColor;
            material.outlineColor = new Color3(edgeColor[0], edgeColor[1], edgeColor[2]);
            material.outlineAlpha = edgeColor[3];
        }
    }
}
